import { isPurchaseOrderNumberValid } from '../../../../../Foundation/validation/validate';
import { setFieldError } from '../../service-flow-configurator/helpers/fieldFactory';
import { BarcodeScanner } from '../../../../barcode-scanner';

export function isPurchaseOrderAction(action) {
  const normalizedAction = action?.toLowerCase();
  return normalizedAction === 'repair' || normalizedAction === 'remake';
}

export function shouldSkipPurchaseOrderValidation({ config, items, action }) {
  if (!config?.ExtendedLogic) {
    return false;
  }

  const warrantyProperty = action?.toLowerCase() === 'remake' ? 'remakeWarranty' : 'repairWarranty';

  return (
    items.length > 0 &&
    items.every((item) => {
      const warrantyValue = item[warrantyProperty] || item.repairWarranty;
      const warrantyEndDate = new Date(warrantyValue).getTime();
      return warrantyEndDate > Date.now();
    })
  );
}

export function renderPurchaseOrderField({ container, config, onChange }) {
  if (!container || !config?.Enabled) {
    return null;
  }

  const field = document.createElement('div');
  field.className = 'dtp-field';

  const inputId = 'service-flow-purchase-order-number';
  const label = document.createElement('label');
  label.setAttribute('for', inputId);

  const labelText = document.createElement('span');
  labelText.className = 'dtp-label-text';
  labelText.textContent = config.Label || 'Purchase order number';

  if (!config.Optional) {
    const requiredMarker = document.createElement('span');
    requiredMarker.className = 'dtp-required-indicator';
    requiredMarker.setAttribute('aria-hidden', 'true');
    requiredMarker.textContent = ' *';
    labelText.appendChild(requiredMarker);
  }

  label.appendChild(labelText);

  if (config.HelpText) {
    const tooltip = document.createElement('span');
    tooltip.className = 'tooltip';
    tooltip.setAttribute('data-title', config.HelpText);

    const tooltipIcon = document.createElement('i');
    tooltipIcon.className = 'eva eva-info-outline';
    tooltipIcon.setAttribute('aria-hidden', 'true');
    tooltip.appendChild(tooltipIcon);
    label.appendChild(tooltip);
  }

  const input = document.createElement('input');
  input.className = 'dtp-input';
  input.id = inputId;
  input.name = inputId;
  input.type = 'text';
  input.autocomplete = 'off';
  input.setAttribute('data-purchase-order-number', '');
  input.setAttribute('aria-required', String(!config.Optional));
  input.maxLength = 20;
  input.setAttribute('data-validation-required-message', config.RequiredMessage || '');
  input.setAttribute('data-validation-invalid-message', config.InvalidMessage || '');
  input.setAttribute('data-validation-field-size-message', config.FieldSizeMessage || '');
  input.setAttribute('data-validation-optional', String(config.Optional));

  if (!config.Optional) {
    input.required = true;
  }

  if (config.DigitsOnlyLength > 0) {
    input.setAttribute('data-validation-digits-only-length', String(config.DigitsOnlyLength));
    input.setAttribute(
      'data-validation-digits-only-error-text',
      config.DigitsOnlyLengthMessage || '',
    );
  }

  if (config.BarcodeHandling) {
    input.setAttribute('data-validation-barcode', 'true');
    new BarcodeScanner(input);
  }

  const errorMessage = document.createElement('span');
  errorMessage.className = 'dtp-input-error-message';
  errorMessage.id = inputId + '-error';
  errorMessage.setAttribute('role', 'alert');
  errorMessage.hidden = true;
  input.setAttribute('aria-describedby', errorMessage.id);

  field.append(label, input);

  field.appendChild(errorMessage);
  container.appendChild(field);

  if (typeof onChange === 'function') {
    input.addEventListener('change', onChange);
  }

  return {
    field: { inputs: [input], errorElement: errorMessage },
    input,
  };
}

export function validatePurchaseOrderField({ input, field }) {
  if (!input || !field) {
    return { isValid: true, message: '' };
  }

  const result = isPurchaseOrderNumberValid(input);
  setFieldError(field, result.isValid ? '' : result.message);
  input.classList.toggle('dtp-input--error', !result.isValid);

  if (result.isValid) {
    input.removeAttribute('aria-invalid');
  } else {
    input.setAttribute('aria-invalid', 'true');
  }

  return result;
}

export class PurchaseOrder {
  constructor({ container, config, action, getItems }) {
    this._container = container;
    this._config = config;
    this._action = action;
    this._getItems = getItems;
    this._validationStarted = false;
    this._visible = false;
    this._input = null;
    this._field = null;
  }

  render() {
    if (!isPurchaseOrderAction(this._action) || !this._config?.Enabled) {
      return;
    }

    const purchaseOrderField = renderPurchaseOrderField({
      container: this._container,
      config: this._config,
      onChange: () => {
        if (this._validationStarted) {
          this.validate();
        }
      },
    });

    this._field = purchaseOrderField?.field;
    this._input = purchaseOrderField?.input;
  }

  updateVisibility() {
    if (!this._container || !this._input) {
      return;
    }

    const items = this._getItems();
    const shouldShow =
      isPurchaseOrderAction(this._action) &&
      items.length > 0 &&
      !shouldSkipPurchaseOrderValidation({
        config: this._config,
        items,
        action: this._action,
      });

    if (this._visible && !shouldShow) {
      this._input.value = '';
      this._validationStarted = false;
      setFieldError(this._field, '');
      this._input.classList.remove('dtp-input--error');
      this._input.removeAttribute('aria-invalid');
    }

    this._container.hidden = !shouldShow;
    this._visible = shouldShow;
  }

  validate() {
    this._validationStarted = true;

    if (!this._input || !this._visible) {
      return { isValid: true, message: '' };
    }

    return validatePurchaseOrderField({ input: this._input, field: this._field });
  }

  getValue() {
    if (!isPurchaseOrderAction(this._action) || !this._visible) {
      return;
    }

    return this._input?.value.trim() || '';
  }
}
